import { useState, useEffect } from 'react';
import { X, ChevronDown, ChevronUp, Copy, RefreshCw, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

function getRiskColor(level) {
  if (!level) return '#10b981';
  if (level === 'critical') return '#dc2626';
  if (level === 'high') return '#ef4444';
  if (level === 'medium') return '#f59e0b';
  return '#10b981';
}

function getRiskBg(level) {
  if (!level) return 'rgba(16,185,129,0.1)';
  if (level === 'critical') return 'rgba(220,38,38,0.12)';
  if (level === 'high') return 'rgba(239,68,68,0.1)';
  if (level === 'medium') return 'rgba(245,158,11,0.1)';
  return 'rgba(16,185,129,0.1)';
}

function ScoreRing({ score, color }) {
  const [display, setDisplay] = useState(0);
  const radius = 42;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (display / 100) * circ;

  useEffect(() => {
    let frame;
    let current = 0;
    const step = score / 45;
    const animate = () => {
      current = Math.min(current + step, score);
      setDisplay(Math.round(current));
      if (current < score) frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [score]);

  return (
    <div style={{ position: 'relative', width: 110, height: 110, flexShrink: 0 }}>
      <svg width="110" height="110" viewBox="0 0 110 110">
        <circle cx="55" cy="55" r={radius} fill="none" stroke="#1f1f2e" strokeWidth="8" />
        <circle cx="55" cy="55" r={radius} fill="none" stroke={color} strokeWidth="8" strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={offset} transform="rotate(-90 55 55)" className="score-ring-fill" />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color, fontSize: 26, fontWeight: 800, letterSpacing: '-0.03em' }}>{display}</span>
      </div>
    </div>
  );
}

function HighlightedText({ text, phrases }) {
  if (!phrases || phrases.length === 0) return <>{text}</>;
  const escaped = phrases.map(p => p.phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const regex = new RegExp(`(${escaped.join('|')})`, 'gi');
  const parts = text.split(regex);
  const phraseSet = new Set(phrases.map(p => p.phrase.toLowerCase()));
  return (
    <>
      {parts.map((part, i) =>
        phraseSet.has(part.toLowerCase())
          ? <mark key={i} className="flagged-phrase">{part}</mark>
          : <span key={i}>{part}</span>
      )}
    </>
  );
}

function generateMarkdown(ticket, analysis) {
  const score = analysis?.assessment?.ambiguityScore ?? 0;
  const riskLevel = (analysis?.assessment?.riskLevel || 'unknown').toUpperCase();
  const hours = Math.round(score * 0.5);
  const det = analysis?.detection || {};
  const ass = analysis?.assessment || {};
  const rem = analysis?.remediation || {};

  return `## ${ticket.title}

**Ambiguity Score:** ${score}/100 | **Risk Level:** ${riskLevel}
**Estimated Rework Risk:** ~${hours} engineering hours
**Vague Phrases Detected:** ${det.vagueWordCount ?? '?'}/${det.totalWordCount ?? '?'} words

---

### Detection Agent — Flagged Phrases
${(det.flaggedPhrases || []).map(p => `- **"${p.phrase}"** — ${p.reason}`).join('\n')}

### Risk Assessment

**Score Breakdown (20 Dimensions, 5 Categories):**
${(ass.scoreBreakdown || []).map(b => `- **${b.dimension}:** ${b.score}/20 — ${b.rationale}`).join('\n')}
${(ass.categoryScores || []).map(cat => cat.dimensions ? cat.dimensions.map(d => `  - ${d.name}: ${d.score}/5 — ${d.evidence || ''}`).join('\n') : '').filter(Boolean).join('\n')}

**Risk Flags:**
${(ass.riskFlags || []).map(f => `- [${(f.severity || '').toUpperCase()}] ${f.title} — ${f.consequence}`).join('\n')}

### Rewrite Agent

**Revised Specification:**
${rem.rewrite || ''}

**Clarifying Questions:**
${(rem.clarifyingQuestions || []).map((q, i) => `${i + 1}. ${q}`).join('\n')}

**Acceptance Criteria:**
${(rem.acceptanceCriteria || []).map(c => `- [ ] ${c}`).join('\n')}

---
*Generated by UnVague — Requirements Ambiguity Engine*`;
}

export default function DetailDrawer({ ticket, onClose, onAcceptRewrite, onReAnalyze }) {
  const [detectionOpen, setDetectionOpen] = useState(false);
  const [barsMounted, setBarsMounted] = useState(false);
  const [expandedCats, setExpandedCats] = useState({});

  useEffect(() => {
    const t = setTimeout(() => setBarsMounted(true), 80);
    return () => clearTimeout(t);
  }, []);

  const analysis = ticket.analysis;
  const detection = analysis?.detection;
  const assessment = analysis?.assessment;
  const remediation = analysis?.remediation;
  const score = assessment?.ambiguityScore ?? 0;
  const riskLevel = assessment?.riskLevel || 'low';
  const riskColor = getRiskColor(riskLevel);
  const hours = Math.round(score * 0.5);
  const CATEGORIES = ['Clarity', 'Completeness', 'Structure', 'Testability', 'Correctness'];

  const handleExport = () => {
    const md = generateMarkdown(ticket, analysis);
    navigator.clipboard.writeText(md).then(() => { toast.success('Copied to clipboard'); }).catch(() => { toast.error('Could not copy to clipboard'); });
  };

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} data-testid="drawer-backdrop" />
      <div className="detail-drawer" data-testid="detail-drawer">
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #1f1f2e', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <h2 style={{ color: '#f4f4f5', fontSize: 17, fontWeight: 700, margin: 0, lineHeight: 1.3 }} data-testid="drawer-title">{ticket.title}</h2>
              {ticket.analyzed_at && <div style={{ color: '#3f3f46', fontSize: 11, marginTop: 5 }}>Analyzed {new Date(ticket.analyzed_at).toLocaleString()}</div>}
            </div>
            <button data-testid="drawer-close-btn" onClick={onClose} style={{ background: 'none', border: 'none', color: '#52525b', cursor: 'pointer', padding: 4, flexShrink: 0 }}><X size={18} /></button>
          </div>
        </div>

        <div style={{ flex: 1, overflow: 'auto', paddingBottom: 100 }} className="scrollbar-hide">
          <div style={{ padding: '16px 24px', borderBottom: '1px solid #1f1f2e' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0 }}>
              {[{ icon: '🔍', label: 'Detection' }, { icon: '📊', label: 'Assessment' }, { icon: '✍️', label: 'Rewrite' }].map((agent, i) => (
                <div key={agent.label} style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, background: '#1a1a24', border: '1px solid #2a2a3a', borderRadius: 10, padding: '8px 14px', minWidth: 90 }}>
                    <span style={{ fontSize: 18 }}>{agent.icon}</span>
                    <span style={{ color: '#a1a1aa', fontSize: 11, fontWeight: 600 }}>{agent.label}</span>
                    <span style={{ color: '#10b981', fontSize: 11 }}>✓ Done</span>
                  </div>
                  {i < 2 && <div style={{ width: 40, height: 2, position: 'relative', overflow: 'hidden', margin: '0 2px' }}><div className="pipeline-dash-track" /></div>}
                </div>
              ))}
            </div>
          </div>

          <div style={{ padding: '20px 24px', borderBottom: '1px solid #1f1f2e' }}>
            <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
              <ScoreRing score={score} color={riskColor} />
              <div style={{ flex: 1 }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', background: getRiskBg(riskLevel), border: `1px solid ${riskColor}40`, borderRadius: 8, padding: '4px 14px', marginBottom: 10 }}>
                  <span style={{ color: riskColor, fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{riskLevel} Risk</span>
                </div>
                <div style={{ color: '#a1a1aa', fontSize: 12, marginBottom: 6 }}>⚠️ Estimated rework: <strong style={{ color: '#f4f4f5' }}>~{hours} engineering hours</strong> if shipped as-is</div>
                {detection && <div style={{ color: '#52525b', fontSize: 12 }}>{detection.vagueWordCount}/{detection.totalWordCount} words flagged as ambiguous{detection.totalWordCount > 0 && ` (${Math.round((detection.vagueWordCount / detection.totalWordCount) * 100)}%)`}</div>}
              </div>
            </div>
          </div>

          {assessment?.scoreBreakdown && assessment.scoreBreakdown.length > 0 && (() => {
            const isNewFormat = !!assessment.categoryScores;
            const displayItems = isNewFormat
              ? CATEGORIES.map(catName => ({
                  name: catName,
                  item: assessment.scoreBreakdown.find(b => b.dimension === catName),
                  catDetail: assessment.categoryScores?.find(c => c.category === catName),
                }))
              : assessment.scoreBreakdown.map(b => ({ name: b.dimension, item: b, catDetail: null }));
            return (
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #1f1f2e' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><span style={{ fontSize: 14 }}>📊</span><span style={{ color: '#f4f4f5', fontSize: 13, fontWeight: 700 }}>Score Breakdown</span></div>
                <span style={{ color: '#52525b', fontSize: 11 }}>{isNewFormat ? '20 dimensions across 5 categories' : `${assessment.scoreBreakdown.length} dimensions`}</span>
              </div>
              {displayItems.map(({ name: catName, item, catDetail }) => {
                const catScore = item?.score ?? catDetail?.score ?? 0;
                const pct = (catScore / 20) * 100;
                const catColor = catScore <= 6 ? '#10b981' : catScore <= 13 ? '#f59e0b' : '#ef4444';
                const isExpanded = expandedCats[catName];
                return (
                  <div key={catName} style={{ marginBottom: 12 }} data-testid={`cat-${catName.toLowerCase()}`}>
                    <div
                      onClick={() => catDetail && setExpandedCats(prev => ({ ...prev, [catName]: !prev[catName] }))}
                      style={{ cursor: catDetail ? 'pointer' : 'default' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
                        <span style={{ color: '#a1a1aa', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                          {catName}
                          {catDetail && (isExpanded ? <ChevronUp size={11} color="#52525b" /> : <ChevronDown size={11} color="#52525b" />)}
                        </span>
                        <span style={{ color: catColor, fontSize: 12, fontWeight: 700 }}>{catScore}<span style={{ color: '#3f3f46', fontWeight: 400 }}>/20</span></span>
                      </div>
                      <div style={{ background: '#1f1f2e', borderRadius: 4, height: 5, overflow: 'hidden', marginBottom: 4 }}>
                        <div style={{ width: barsMounted ? `${pct}%` : '0%', height: '100%', background: catColor, borderRadius: 4, transition: barsMounted ? 'width 0.8s cubic-bezier(0.16,1,0.3,1)' : 'none' }} />
                      </div>
                      {item?.rationale && <div style={{ color: '#3f3f46', fontSize: 11, lineHeight: 1.4 }}>{item.rationale}</div>}
                    </div>
                    {isExpanded && catDetail?.dimensions && (
                      <div style={{ marginTop: 8, marginLeft: 12, paddingLeft: 12, borderLeft: '2px solid #1f1f2e' }}>
                        {catDetail.dimensions.map((dim) => {
                          const subPct = (dim.score / 5) * 100;
                          const subColor = dim.score <= 1 ? '#10b981' : dim.score <= 3 ? '#f59e0b' : '#ef4444';
                          return (
                            <div key={dim.id} style={{ marginBottom: 10 }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 }}>
                                <span style={{ color: '#71717a', fontSize: 11, fontWeight: 500 }}>{dim.name}</span>
                                <span style={{ color: subColor, fontSize: 11, fontWeight: 700 }}>{dim.score}<span style={{ color: '#3f3f46', fontWeight: 400 }}>/5</span></span>
                              </div>
                              <div style={{ background: '#1f1f2e', borderRadius: 3, height: 3, overflow: 'hidden', marginBottom: 3 }}>
                                <div style={{ width: barsMounted ? `${subPct}%` : '0%', height: '100%', background: subColor, borderRadius: 3, transition: barsMounted ? 'width 0.6s cubic-bezier(0.16,1,0.3,1)' : 'none' }} />
                              </div>
                              {dim.evidence && <div style={{ color: '#3f3f46', fontSize: 10, lineHeight: 1.3, fontStyle: 'italic' }}>{dim.evidence}</div>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            );
          })()}

          {assessment?.riskFlags && assessment.riskFlags.length > 0 && (
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #1f1f2e' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}><span style={{ fontSize: 14 }}>🚩</span><span style={{ color: '#f4f4f5', fontSize: 13, fontWeight: 700 }}>Risk Flags</span></div>
              {assessment.riskFlags.map((flag, i) => {
                const sevColor = flag.severity === 'high' ? '#ef4444' : flag.severity === 'medium' ? '#f59e0b' : '#10b981';
                const sevBg = flag.severity === 'high' ? 'rgba(239,68,68,0.1)' : flag.severity === 'medium' ? 'rgba(245,158,11,0.1)' : 'rgba(16,185,129,0.1)';
                return (
                  <div key={i} style={{ background: '#141419', border: '1px solid #1f1f2e', borderRadius: 8, padding: '10px 14px', marginBottom: 8, borderLeft: `3px solid ${sevColor}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ background: sevBg, color: sevColor, fontSize: 10, fontWeight: 700, padding: '1px 8px', borderRadius: 10, textTransform: 'uppercase' }}>{flag.severity}</span>
                      <span style={{ color: '#f4f4f5', fontSize: 12, fontWeight: 600 }}>{flag.title}</span>
                    </div>
                    <div style={{ color: '#71717a', fontSize: 11 }}>{flag.consequence}</div>
                  </div>
                );
              })}
            </div>
          )}

          {remediation?.rewrite && (
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #1f1f2e' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}><span style={{ fontSize: 14 }}>🔄</span><span style={{ color: '#f4f4f5', fontSize: 13, fontWeight: 700 }}>Before → After</span></div>
              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ flex: 1, background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.15)', borderRadius: 8, padding: '12px 14px' }}>
                  <div style={{ color: '#ef4444', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Original</div>
                  <div style={{ color: '#a1a1aa', fontSize: 12, lineHeight: 1.6 }}><HighlightedText text={ticket.text} phrases={detection?.flaggedPhrases || []} /></div>
                </div>
                <div style={{ flex: 1, background: 'rgba(16,185,129,0.05)', border: '1px solid rgba(16,185,129,0.15)', borderRadius: 8, padding: '12px 14px' }}>
                  <div style={{ color: '#10b981', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>AI Rewrite</div>
                  <div style={{ color: '#a1a1aa', fontSize: 12, lineHeight: 1.6 }}>{remediation.rewrite}</div>
                </div>
              </div>
            </div>
          )}

          {remediation?.clarifyingQuestions && remediation.clarifyingQuestions.length > 0 && (
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #1f1f2e' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}><span style={{ fontSize: 14 }}>❓</span><span style={{ color: '#f4f4f5', fontSize: 13, fontWeight: 700 }}>Clarifying Questions for Your Team</span></div>
              {remediation.clarifyingQuestions.map((q, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
                  <span style={{ color: '#6366f1', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{i + 1}.</span>
                  <span style={{ color: '#a1a1aa', fontSize: 12, lineHeight: 1.5 }}>{q}</span>
                </div>
              ))}
            </div>
          )}

          {remediation?.acceptanceCriteria && remediation.acceptanceCriteria.length > 0 && (
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #1f1f2e' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}><span style={{ fontSize: 14 }}>✅</span><span style={{ color: '#f4f4f5', fontSize: 13, fontWeight: 700 }}>Generated Acceptance Criteria</span></div>
              {remediation.acceptanceCriteria.map((c, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 8, alignItems: 'flex-start' }}>
                  <CheckCircle size={13} color="#52525b" style={{ flexShrink: 0, marginTop: 2 }} />
                  <span style={{ color: '#a1a1aa', fontSize: 12, lineHeight: 1.5 }}>{c}</span>
                </div>
              ))}
            </div>
          )}

          {detection?.flaggedPhrases && detection.flaggedPhrases.length > 0 && (
            <div style={{ padding: '16px 24px', borderBottom: '1px solid #1f1f2e' }}>
              <button data-testid="detection-toggle" onClick={() => setDetectionOpen(v => !v)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', background: 'none', border: 'none', cursor: 'pointer', color: '#a1a1aa', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', padding: 0 }}>
                <span>🔍 Detection Agent Raw Output</span>
                {detectionOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
              {detectionOpen && (
                <div style={{ marginTop: 12 }}>
                  {detection.flaggedPhrases.map((p, i) => (
                    <div key={i} style={{ marginBottom: 8, padding: '8px 12px', background: '#141419', borderRadius: 6 }}>
                      <div style={{ color: '#fca5a5', fontSize: 12, fontFamily: 'monospace', marginBottom: 3 }}>"{p.phrase}"</div>
                      <div style={{ color: '#71717a', fontSize: 11 }}>{p.reason}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{ position: 'sticky', bottom: 0, left: 0, right: 0, borderTop: '1px solid #1f1f2e', background: '#0d0d12', padding: '14px 20px', display: 'flex', gap: 8 }}>
          <button data-testid="accept-rewrite-btn" onClick={() => onAcceptRewrite(ticket)} style={{ flex: 2, background: '#10b981', border: 'none', borderRadius: 8, padding: '9px 14px', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'background 0.2s' }} onMouseOver={e => e.currentTarget.style.background = '#059669'} onMouseOut={e => e.currentTarget.style.background = '#10b981'}>Accept Rewrite</button>
          <button data-testid="re-analyze-btn" onClick={() => onReAnalyze(ticket)} style={{ flex: 1, background: 'transparent', border: '1px solid #2a2a3a', borderRadius: 8, padding: '9px 14px', color: '#a1a1aa', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}><RefreshCw size={12} /> Re-Analyze</button>
          <button data-testid="export-btn" onClick={handleExport} style={{ flex: 1, background: 'transparent', border: '1px solid #2a2a3a', borderRadius: 8, padding: '9px 14px', color: '#a1a1aa', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}><Copy size={12} /> Export</button>
        </div>
      </div>
    </>
  );
}
